/**
 * Meme loader: GIF via URL (remote or same-origin). No CSS spin — the GIF provides motion.
 *  */

import { cn } from '@/lib/utils';

export type MemeMediaLayout =
  | 'inline-xs'
  | 'inline-sm'
  | 'inline-md'
  | 'inline-lg'
  | 'panel'
  | 'panel-lg'
  | 'overlay';

/** GIF memes need larger boxes than CSS ring spinners so they stay readable. */
const MEME_LAYOUT_BOX: Record<MemeMediaLayout, string> = {
  overlay: 'h-80 w-80 max-h-[55vh] max-w-[min(100vw-2rem,20rem)]',
  'panel-lg': 'h-44 w-44 md:h-52 md:w-52 max-w-full',
  panel: 'h-36 w-36 md:h-40 md:w-40 max-w-full',
  'inline-xs': 'h-7 w-7',
  'inline-sm': 'h-9 w-9',
  'inline-md': 'h-11 w-11',
  'inline-lg': 'h-44 w-44 max-w-[min(100%,18rem)] sm:h-52 sm:w-52',
};

interface MemeMediaViewProps {
  src: string;
  layout: MemeMediaLayout;
  className?: string;
  /** When true, exposes role="status" and sr-only label (inline spinners). */
  labelled?: boolean;
}

export function MemeMediaView({ src, layout, className, labelled }: MemeMediaViewProps) {
  const box = MEME_LAYOUT_BOX[layout];
  return (
    <div
      className={cn('inline-flex items-center justify-center overflow-hidden rounded-md', box, className)}
      role={labelled ? 'status' : undefined}
    >
      <img
        src={src}
        alt=""
        className="h-full w-full object-contain"
        draggable={false}
        aria-hidden="true"
      />
      {labelled ? <span className="sr-only">Loading</span> : null}
    </div>
  );
}
